import { Field, Input, cn } from '@provenance/design';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * The colour a mark prints in.
 *
 * Per mark, not per chamber or per document type — on a TAFTA certificate the "issued at"
 * line is red while the certificate number beside it stays black, so one colour for a whole
 * document would be unusable for the chambers that need colour at all.
 *
 * A picker AND a hex field, both editable. The picker is how someone finds a colour; the
 * hex field is how a chamber enters the one from their brand guidelines, which is the case
 * that actually matters here.
 *
 * **Readability is warned about, never enforced.** A pale grey certificate number renders
 * perfectly and is unreadable on paper — but a chamber may have a reason we do not know,
 * and this is configuration rather than a correctness rule. The same principle as §2.3: it
 * flags, it does not gate.
 */
export function ColourField({
  value,
  onChange,
  label = 'Text colour',
}: {
  value: string;
  onChange: (hex: string) => void;
  label?: string;
}) {
  // Typing a hex goes through local state so a half-typed "#B3" is not pushed upstream as
  // a colour, which would make the canvas flicker through nonsense on every keystroke.
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = (next: string) => {
    const hex = normalise(next);
    if (hex) onChange(hex);
    else setDraft(value);
  };

  const warning = readabilityWarning(value);

  return (
    <Field
      label={label}
      help="Printed in this colour on the certificate. Most marks are black."
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={`${label} picker`}
            value={normalise(value) ?? '#000000'}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="size-9 shrink-0 cursor-pointer rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-[var(--pv-card)] p-1"
          />
          <Input
            aria-label={`${label} hex code`}
            value={draft}
            spellCheck={false}
            className="font-[family-name:var(--pv-font-mono)] uppercase"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit((e.target as HTMLInputElement).value);
              }
            }}
          />
        </div>

        {warning && (
          <p
            className={cn(
              'flex items-start gap-1.5 rounded-[var(--pv-radius-sm)] px-2 py-1.5 [font-size:12px] leading-snug',
              'bg-[var(--pv-clay-soft)] text-[var(--pv-clay-deep)]',
            )}
          >
            <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0" />
            <span>{warning}</span>
          </p>
        )}
      </div>
    </Field>
  );
}

/** `#RGB`, `RRGGBB` and anything in between, or null when it is not a colour at all. */
function normalise(input: string): string | null {
  const raw = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw.split('').map((c) => c + c).join('').toUpperCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toUpperCase()}`;
  return null;
}

/**
 * Whether this colour will survive being printed and photocopied.
 *
 * Contrast is against WHITE, because a certificate is printed on paper — not against the
 * editor's own background, which is a warm off-white and would flatter a colour that will
 * be washed out on the real thing.
 *
 * The 4.5:1 threshold is WCAG's floor for body text. A certificate number is transcribed by
 * a customs officer from a page that may well be a third-generation scan, so the bar is at
 * least as high as a screen's.
 */
export function readabilityWarning(hex: string): string | null {
  const rgb = toRgb(hex);
  if (!rgb) return 'That is not a colour we can read. Use a hex code like #B3121B.';

  const ratio = contrastAgainstWhite(rgb);
  if (ratio >= 4.5) return null;
  if (ratio >= 3) {
    return 'This is faint on white paper and may not survive a photocopy or a fax. It will still print exactly as chosen.';
  }
  return 'This is very close to white and will be all but invisible on a printed certificate. It will still print exactly as chosen.';
}

function toRgb(hex: string): [number, number, number] | null {
  const normalised = normalise(hex);
  if (!normalised) return null;
  const n = Number.parseInt(normalised.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function contrastAgainstWhite([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return 1.05 / (luminance + 0.05);
}
